import { useQuery } from '@tanstack/react-query';

import { getCurrentInstitute, INSTITUTE_QUERY_KEYS, type CurrentInstitute } from './api';

export interface CurrentInstituteState {
  instituteId: string | null;
  role: CurrentInstitute['role'] | null;
  isPending: boolean;
  error: Error | null;
}

/**
 * "Which institute am I staff at, and in what role?"
 *
 * Every institute page needs this before it can query anything else, and the
 * same `useQuery` + `data?.institute_id ?? null` block was being copy-pasted
 * into each one. Shared cache key, so mounting this in several components on
 * one screen costs a single round-trip.
 *
 * Lives under `features/institute/` rather than `shared/hooks/` because it
 * imports from `./api` — a shared-layer module importing a feature would invert
 * the dependency direction that `madge --circular` enforces.
 */
export function useCurrentInstitute(): CurrentInstituteState {
  const query = useQuery({
    queryKey: INSTITUTE_QUERY_KEYS.current,
    queryFn: getCurrentInstitute,
  });

  return {
    instituteId: query.data?.institute_id ?? null,
    role: query.data?.role ?? null,
    isPending: query.isPending,
    error: query.error,
  };
}
